import { InboxSort, NotificationPriorityHelpers } from 'src/helpers/NotificationPriorityHelpers';
import { mutate } from 'swr';
import { SWRInfiniteKeyedMutator, unstable_serialize } from 'swr/infinite';

import { publicConfig } from '@public-config';

import { InboxNotification, Notification } from 'models';
import { InboxQuery, NotificationHelpers } from 'helpers/NotificationHelpers';

import { DEFAULT_INBOX_FILTERS, InboxFilters, state as inboxState } from 'state/inbox';

import type { NotificationCounts } from '@/collections/notifications/endpoints/counts';

import { ApiService } from './ApiService';
import { InternalTrackingService } from './InternalTrackingService';
import { LocalNotificationService } from './LocalNotificationService';
import { PayloadSDK } from './PayloadSDK';

type InboxFilterQuery = InboxQuery & {
    team?: string | null;
    company?: string | null;
    unread?: boolean;
    archived?: boolean;
};
type InboxListQuery = InboxFilterQuery & { sort: InboxSort };
type InboxCursor = Pick<InboxNotification, 'rank' | 'createdAt'>;

const INBOX_PAGE_SIZE = 20;
const INBOX_KEY = '/notifications/inbox';
const COUNTS_KEY = '/notifications/counts';

// The inbox lists on screen, to refresh or patch them from outside (their SWR keys are internal).
const inboxLists = new Set<SWRInfiniteKeyedMutator<InboxNotification[][]>>();

// Every inbox list opened this session, by SWR key. A list off screen keeps its cache, so moving a notification
// between the inbox and archived views patches it too rather than refetching it.
const inboxListQueries = new Map<string, InboxListQuery>();

// Every notification shown this session, to preview a view locally while it loads
const loadedNotifications = new Map<string, InboxNotification>();

const toSearchParams = ({ types, team, company, unread, archived }: InboxFilterQuery) => {
    const params = new URLSearchParams();

    if (types?.length) params.set('types', types.join(','));
    if (team) params.set('team', team);
    if (company) params.set('company', company);
    if (unread) params.set('unread', 'true');
    if (archived) params.set('archived', 'true');

    return params;
};

const moveInInboxLists = async (notification: InboxNotification) => {
    loadedNotifications.set(notification.id, notification);

    await Promise.all(
        [...inboxListQueries].map(async ([listKey, query]) => {
            // The company filter is only resolved server-side, so those lists just drop the notification and refetch.
            const canInsert = !query.company;
            const moved = { ...notification, rank: NotificationPriorityHelpers.getRank(notification, query.sort) };
            const pages =
                (await mutate<InboxNotification[][]>(
                    listKey,
                    (cached) =>
                        cached && NotificationHelpers.placeInList(cached, moved, query, INBOX_PAGE_SIZE, canInsert),
                    { revalidate: false },
                )) ?? [];

            // The list hook refetches any page whose own cache entry differs from the list, so keep those in step
            // (or clear them, when the list has to refetch).
            await Promise.all(
                pages.map((page, index) =>
                    mutate(
                        NotificationService.inboxKey(query, toCursor(pages[index - 1])),
                        canInsert ? page : undefined,
                        { revalidate: false },
                    ),
                ),
            );
        }),
    );
};

// Bulk archiving can't be mirrored locally, so cached archived lists are cleared and refetch when next opened.
const dropCachedArchivedPages = () =>
    mutate(
        (key) => Array.isArray(key) && key[0] === INBOX_KEY && Boolean((key[1] as InboxFilterQuery).archived),
        undefined,
        { revalidate: false },
    );

const toCursor = (previousPage?: InboxNotification[]): InboxCursor | undefined => {
    const lastNotification = previousPage?.at(-1);

    return lastNotification && { rank: lastNotification.rank, createdAt: lastNotification.createdAt };
};

const NotificationService = {
    pageSize: INBOX_PAGE_SIZE,

    inboxKey: (query: InboxListQuery, before?: InboxCursor) => [INBOX_KEY, query, before] as const,

    countsKey: (team: string | null = null, company: string | null = null, archived = false) =>
        [COUNTS_KEY, team, company, archived] as const,

    inbox: async ({
        before,
        limit = INBOX_PAGE_SIZE,
        sort,
        ...filter
    }: InboxListQuery & { before?: InboxCursor; limit?: number }) => {
        const params = toSearchParams(filter);

        params.set('limit', String(limit));
        params.set('sort', sort);

        if (before) {
            params.set('before', before.createdAt);
            params.set('beforeRank', String(before.rank));
        }

        return await ApiService.get<InboxNotification[]>(`${publicConfig.urls.api}/notifications/inbox?${params}`);
    },

    counts: async (team: string | null, company: string | null = null, archived = false) => {
        const params = toSearchParams({ team, company, archived });

        return await ApiService.get<NotificationCounts>(`${publicConfig.urls.api}/notifications/counts?${params}`);
    },

    refreshInbox: async () => {
        await Promise.all([refreshCounts(), ...[...inboxLists].map((mutateList) => mutateList())]);
    },

    markReadLocally: async (notificationId: string, readAt: string) => {
        const markRead = (pages: InboxNotification[][] = []) =>
            pages.map((page) =>
                page.map((notification) =>
                    notification.id === notificationId ? { ...notification, readAt } : notification,
                ),
            );

        await Promise.all([
            refreshCounts(),
            ...[...inboxLists].map((mutateList) => mutateList(markRead, { revalidate: false })),
        ]);
    },

    registerInboxList: (query: InboxListQuery, mutateList: SWRInfiniteKeyedMutator<InboxNotification[][]>) => {
        inboxListQueries.set(
            unstable_serialize(() => NotificationService.inboxKey(query)),
            query,
        );
        inboxLists.add(mutateList);

        return () => {
            inboxLists.delete(mutateList);
        };
    },

    rememberLoaded: (notifications: InboxNotification[]) => {
        for (const notification of notifications) {
            loadedNotifications.set(notification.id, notification);
        }
    },

    getLoaded: () => [...loadedNotifications.values()],

    receiveNotification: (notification: Notification) => {
        LocalNotificationService.showToast({
            // @ts-ignore
            title: notification.app.title,
            // @ts-ignore
            description: notification.app.text,
            // @ts-ignore
            url: notification.app.url,
        });
    },

    openInbox: (filters: Partial<InboxFilters> = {}) => {
        InternalTrackingService.track('notifications.open-inbox');
        inboxState.opened = true;
        NotificationService.setFilters(filters);
    },

    setFilters: (filters: Partial<InboxFilters>) => {
        Object.assign(inboxState.filters, filters);
    },

    resetFilters: () => {
        inboxState.filters = { ...DEFAULT_INBOX_FILTERS };
    },

    closeInbox: () => {
        inboxState.opened = false;
    },

    read: async (notification: Notification) => {
        try {
            await ApiService.post(`${publicConfig.urls.api}/notifications/${notification.id}/read`);
            await NotificationService.markReadLocally(notification.id, new Date().toISOString());
        } catch {
            // This call can fail if a users isn't authenticated
            console.error(`Failed to read notification: ${notification.id}`);
        } finally {
            InternalTrackingService.track('notifications.read', {
                notification,
            });
        }
    },

    archive: async (notification: InboxNotification) => {
        InternalTrackingService.track('notifications.archive', {
            notification,
        });

        const archivedAt = new Date().toISOString();

        await moveInInboxLists({ ...notification, deletedAt: archivedAt, readAt: notification.readAt ?? archivedAt });

        try {
            await ApiService.post(`${publicConfig.urls.api}/notifications/${notification.id}/delete`);
            await refreshCounts();
        } catch (error) {
            await NotificationService.refreshInbox();

            throw error;
        }

        LocalNotificationService.show({
            message: 'Notification archived',
            actions: [{ label: 'Undo', onClick: () => NotificationService.restore(notification).then() }],
        });
    },

    restore: async (notification: InboxNotification) => {
        InternalTrackingService.track('notifications.restore', {
            notification,
        });

        const params = new URLSearchParams({ unread: String(!notification.readAt) });

        await moveInInboxLists({ ...notification, deletedAt: undefined });

        try {
            await ApiService.post(`${publicConfig.urls.api}/notifications/${notification.id}/restore?${params}`);
            await refreshCounts();
        } catch (error) {
            await NotificationService.refreshInbox();

            throw error;
        }
    },

    readAll: async (filter: InboxFilterQuery = {}) => {
        InternalTrackingService.track('notifications.read-all');

        await ApiService.post(`${publicConfig.urls.api}/notifications/read-all?${toSearchParams(filter)}`);
        await NotificationService.refreshInbox();
    },

    archiveAll: async (filter: InboxFilterQuery = {}) => {
        InternalTrackingService.track('notifications.archive-all');

        await ApiService.post(`${publicConfig.urls.api}/notifications/archive-all?${toSearchParams(filter)}`);
        await Promise.all([NotificationService.refreshInbox(), dropCachedArchivedPages()]);
    },

    handle: async (notification: Notification) => {
        InternalTrackingService.track('notifications.handled', {
            notification,
        });

        await ApiService.post(`${publicConfig.urls.api}/notifications/${notification.id}/handle`);
    },

    get: async (id: string) => {
        return await PayloadSDK.findByID({
            collection: 'notifications',
            id,
        });
    },
};

const refreshCounts = () => mutate((key) => Array.isArray(key) && key[0] === COUNTS_KEY);

export { NotificationService };
